import React, { useRef, useState, useEffect } from 'react';
import { Camera, Loader2, X, Plus } from 'lucide-react';
import ProductExtractionResults from './ProductExtractionResults';
import PhotoValidationModal from './PhotoValidationModal';
import { PhotoExtractionStatus } from './PhotoExtractionStatus';
import { isImageFinal, type ServerImageLike } from '../../features/humanSurvey/photoUploadSessionSync';
import { isImageInFlight } from '../../features/humanSurvey/photoUploadValidation';
import InBrowserCamera from './InBrowserCamera';

const API_URL = import.meta.env.VITE_API_URL || '';
// A photo upload must eventually settle. Without this a hung request left the placeholder in
// state forever, and since Next is gated on in-flight uploads that means the respondent cannot
// continue — the catch block below can only free them if the promise actually rejects.
// Generous because respondents are on phone networks uploading multi-MB images.
const UPLOAD_TIMEOUT_MS = 90_000;

interface ExtractedProduct {
  name: string;
  brand?: string | null;
  category?: string | null;
  confidence: number;
  quantity?: number | null;
}

interface UploadedImage {
  s3_key: string;
  url: string;
  extracted_products: ExtractedProduct[];
  extraction_status: 'pending' | 'processing' | 'completed' | 'failed' | 'uploading';
  image_quality?: string | null;
  upload_timestamp: string;
  localPreview?: string;
  rejection_reason?: string;
  text_input?: string; // Per-image text input when extraction fails
}

interface MobileCameraUploadProps {
  sessionId: number;
  questionId: string;
  questionText?: string;
  maxImages?: number;
  onImagesChange: (images: UploadedImage[]) => void;
  images: UploadedImage[];
  disabled?: boolean;
  onTextFallback?: (text: string) => void;
  textFallback?: string;
}

const MobileCameraUpload: React.FC<MobileCameraUploadProps> = ({
  sessionId,
  questionId,
  questionText = "",
  maxImages = 3,
  onImagesChange,
  images,
  disabled = false,
}) => {
  console.log('[MobileCameraUpload] Render - sessionId:', sessionId, 'questionId:', questionId, 'disabled:', disabled, 'images:', images.length);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const objectUrlsRef = useRef<string[]>([]);
  const imagesRef = useRef<UploadedImage[]>(images);
  const isMountedRef = useRef(true);
  const [showCamera, setShowCamera] = useState(false);
  // Set when getUserMedia is unusable on this device/browser (unsupported, permission denied,
  // no camera). Once true we stop offering the in-page camera and use the OS file/camera input,
  // so a respondent is never trapped behind a camera that cannot open.
  const [cameraUnavailable, setCameraUnavailable] = useState(false);
  // Why the in-page camera is unavailable. Persistent (unlike `error`, which self-clears) because
  // it explains a permanent change in how this question behaves.
  const [cameraFallbackReason, setCameraFallbackReason] = useState<string | null>(null);

  // Keep ref in sync with prop
  useEffect(() => {
    imagesRef.current = images;
  }, [images]);

  const [error, setError] = useState<string | null>(null);
  const [validationModal, setValidationModal] = useState<{
    isOpen: boolean;
    error: string;
    s3Key: string;
  }>({ isOpen: false, error: '', s3Key: '' });

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Revoke all object URLs we created
      objectUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
    };
  }, []);

  // Auto-clear error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const handleCapture = async (event: React.ChangeEvent<HTMLInputElement>) => {
    console.log('[MobileCameraUpload] handleCapture called');
    const fileList = event.target.files;
    console.log('[MobileCameraUpload] fileList:', fileList, 'length:', fileList?.length);

    if (!fileList?.length) {
      console.log('[MobileCameraUpload] No files selected, returning');
      return;
    }

    // IMPORTANT: Copy files to array BEFORE resetting input, as reset clears the FileList
    const filesArray = Array.from(fileList);
    console.log('[MobileCameraUpload] filesArray copied:', filesArray.length);

    // Reset file input to allow re-selecting the same file
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    processFiles(filesArray);
  };

  // Shared by the OS file input and the in-page camera, so both go through identical upload,
  // extraction and validation handling.
  const processFiles = (filesArray: File[]) => {
    const currentImageCount = imagesRef.current.length;
    const availableSlots = maxImages - currentImageCount;
    console.log('[MobileCameraUpload] currentImageCount:', currentImageCount, 'availableSlots:', availableSlots);

    if (availableSlots <= 0) {
      setError(`Maximum of ${maxImages} images reached`);
      return;
    }

    setError(null);

    const filesToUpload = filesArray.slice(0, availableSlots);
    console.log('[MobileCameraUpload] filesToUpload:', filesToUpload.length);

    // Process each file independently (parallel uploads)
    filesToUpload.forEach((file, index) => {
      console.log('[MobileCameraUpload] Processing file', index, 'size:', file.size);
      if (file.size > 15 * 1024 * 1024) {
        setError('File size exceeds 15MB limit');
        return;
      }

      const localPreview = URL.createObjectURL(file);
      objectUrlsRef.current.push(localPreview);

      // Generate a temporary key for tracking this upload
      const tempKey = `uploading-${Date.now()}-${index}`;

      // Add placeholder image immediately with 'uploading' status
      const placeholderImage: UploadedImage = {
        s3_key: tempKey,
        url: '',
        extracted_products: [],
        extraction_status: 'uploading',
        upload_timestamp: new Date().toISOString(),
        localPreview
      };

      // Add to images immediately so user sees preview (use ref for latest state)
      const updatedImages = [...imagesRef.current, placeholderImage];
      imagesRef.current = updatedImages;
      console.log('[MobileCameraUpload] Calling onImagesChange with', updatedImages.length, 'images');
      onImagesChange(updatedImages);

      // Upload in background
      const uploadFile = async () => {
        console.log('[MobileCameraUpload] Starting upload for', tempKey);
        try {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('question_text', questionText);

          console.log('[MobileCameraUpload] Fetching', `${API_URL}/api/photo-upload/upload-direct/${sessionId}/${questionId}`);
          const response = await fetch(
            `${API_URL}/api/photo-upload/upload-direct/${sessionId}/${questionId}`,
            {
              method: 'POST',
              body: formData,
              credentials: 'include',
              signal: AbortSignal.timeout(UPLOAD_TIMEOUT_MS)
            }
          );

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.detail || 'Upload failed');
          }

          const result = await response.json();

          // Replace placeholder with real image data (use ref for latest state)
          const newImages = imagesRef.current.map(img =>
            img.s3_key === tempKey
              ? { ...result, localPreview }
              : img
          );
          imagesRef.current = newImages;
          // Same unmount rule as the poll: a late upload must not write into the next question.
          if (!isMountedRef.current) return;
          onImagesChange(newImages);

          // Only show validation modal for IMMEDIATE failures
          const isImmediateFailure = result.extraction_status === 'failed' && !result.show_text_fallback;
          if (isImmediateFailure) {
            setValidationModal({
              isOpen: true,
              error: result.rejection_reason || 'Image could not be processed. Please try again.',
              s3Key: result.s3_key
            });
          }
        } catch (err) {
          // Remove the placeholder on error (use ref for latest state)
          URL.revokeObjectURL(localPreview);
          objectUrlsRef.current = objectUrlsRef.current.filter(u => u !== localPreview);
          const filteredImages = imagesRef.current.filter(img => img.s3_key !== tempKey);
          imagesRef.current = filteredImages;
          if (!isMountedRef.current) return;
          onImagesChange(filteredImages);
          const timedOut = err instanceof DOMException && err.name === 'TimeoutError';
          setError(timedOut
            ? "That photo took too long to upload — please check your connection and try again."
            : err instanceof Error ? err.message : 'Upload failed');
        }
      };

      uploadFile();
    });
  };

  const handleRemoveImage = (s3Key: string) => {
    const imageToRemove = images.find(img => img.s3_key === s3Key);
    if (imageToRemove?.localPreview) {
      URL.revokeObjectURL(imageToRemove.localPreview);
      objectUrlsRef.current = objectUrlsRef.current.filter(u => u !== imageToRemove.localPreview);
    }
    const updatedImages = images.filter(img => img.s3_key !== s3Key);
    onImagesChange(updatedImages);
  };

  const handleRetake = () => {
    const s3Key = validationModal.s3Key;
    setValidationModal({ isOpen: false, error: '', s3Key: '' });
    handleRemoveImage(s3Key);
    // User will tap "Take Photo" button to retake - more reliable on mobile
  };

  const handleContinueAnyway = () => {
    setValidationModal({ isOpen: false, error: '', s3Key: '' });
  };

  const canAddMore = images.length < maxImages;

  // In-page camera by default so the respondent stays in the survey; the OS input only as a
  // fallback. Handing off to the camera app backgrounds the browser mid-survey, which on iOS can
  // discard the page and lose answers.
  const openCamera = () => {
    if (disabled) return;
    if (cameraUnavailable) {
      fileInputRef.current?.click();
      return;
    }
    setShowCamera(true);
  };

  const handleCameraCapture = (file: File) => {
    setShowCamera(false);
    processFiles([file]);
  };

  const handleCameraUnavailable = (reason: string, permanent: boolean) => {
    console.warn('[MobileCameraUpload] in-page camera failed:', reason, '(permanent:', permanent, ')');
    setShowCamera(false);

    if (!permanent) {
      // A transient interruption — usually play() being cut off when the camera is reopened
      // quickly. Keep the in-page camera and let them tap again. Handing off to the OS camera app
      // here would be far worse than the glitch: the app switch backgrounds the browser and can
      // discard the survey, losing every answer so far.
      setError(reason);
      return;
    }

    setCameraUnavailable(true);
    // Deliberately NOT setError: the OS picker opens over the page immediately and the transient
    // error auto-clears after 5s, so the respondent would never read it — they would just see the
    // UI change for no visible reason. The reason is kept as persistent helper text under the
    // button instead, and the console warn keeps it debuggable.
    setCameraFallbackReason(reason);
    // Fall straight through to the OS picker so the tap still results in a photo.
    fileInputRef.current?.click();
  };

  // Poll while any image the badge shows as pending has no final verdict — the same rule the
  // badge uses, so a tile can never spin with no poll behind it.
  const awaitsVerdict = (img: ServerImageLike) => img.extraction_status !== 'uploading' && !isImageFinal(img);
  const hasProcessingImages = images.some(awaitsVerdict);
  useEffect(() => {
    if (!hasProcessingImages) return;

    const pollInterval = setInterval(async () => {
      const processingImages = images.filter(awaitsVerdict);
      if (processingImages.length === 0) {
        clearInterval(pollInterval);
        return;
      }

      let hasUpdates = false;
      const updatedImages = await Promise.all(
        images.map(async (img) => {
          if (!awaitsVerdict(img)) return img;

          try {
            const response = await fetch(
              `${API_URL}/api/photo-upload/extraction-status/${encodeURIComponent(img.s3_key)}`,
              { credentials: 'include' }
            );
            if (response.ok) {
              const result = await response.json();
              if (result.status === 'completed') {
                hasUpdates = true;
                return {
                  ...img,
                  extraction_status: result.extraction_status,
                  extracted_products: result.extracted_products || [],
                  image_quality: result.image_quality,
                  rejection_reason: result.rejection_reason
                };
              }
            }
          } catch (err) {
            console.error('Polling error:', err);
          }
          return img;
        })
      );

      // Only skip the write-back if we're UNMOUNTED. clearInterval stops future ticks but not
      // an in-flight one, which would otherwise land a photo-upload object in the next
      // question's answer state. Deliberately a ref, not a per-effect flag: this effect
      // re-runs on every `images`/`onImagesChange` change (the parent's handler isn't
      // memoized), so a flag set in cleanup would also discard perfectly good updates.
      if (hasUpdates && isMountedRef.current) {
        onImagesChange(updatedImages);
      }
    }, 2000);

    return () => clearInterval(pollInterval);
  }, [hasProcessingImages, images, onImagesChange]);

  const inputId = `camera-input-${questionId}`;

  return (
    <div className="space-y-4">
      <input
        id={inputId}
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleCapture}
        className="hidden"
      />

      {images.length === 0 ? (
        <button
          type="button"
          onClick={openCamera}
          disabled={disabled}
          className={`w-full py-8 border-2 border-dashed border-[#07E5D1] rounded-xl
                     bg-[#E8FCFA] hover:bg-[#d0f9f5] transition-colors flex flex-col
                     items-center justify-center gap-3 cursor-pointer
                     ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <Camera className="w-10 h-10 text-[#07E5D1]" />
          <span className="text-lg font-medium text-[#06D4C2]">Take Photo</span>
          <span className="text-sm text-[#07E5D1]">Tap to open the camera</span>
        </button>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4">
            {images.map((image) => (
              <div key={image.s3_key} className="relative bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="aspect-video relative bg-gray-100">
                  <img
                    src={image.localPreview || `${API_URL}${image.url}`}
                    alt="Uploaded photo"
                    className="w-full h-full object-cover"
                  />
                  <button
                    onClick={() => handleRemoveImage(image.s3_key)}
                    aria-label="Remove photo"
                    className="absolute top-2 right-2 z-10 p-1.5 bg-red-500 text-white rounded-full
                               hover:bg-red-600 transition-colors shadow-md"
                  >
                    <X className="w-4 h-4" />
                  </button>

                  {image.extraction_status === 'uploading' && (
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                      <div className="bg-white rounded-lg px-4 py-2 flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin text-[#07E5D1]" />
                        <span className="text-sm font-medium">Uploading...</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="p-3 pb-0">
                  <PhotoExtractionStatus image={image} />
                </div>

                {image.extraction_status === 'completed' && image.extracted_products?.length > 0 && (
                  <ProductExtractionResults
                    products={image.extracted_products}
                    showImage={false}
                  />
                )}

                {/* Only a REAL rejection gets a description box — not merely "no products yet",
                    which is also true of every still-processing image. */}
                {image.extraction_status === 'failed' && (
                  <div className="p-3 pt-1">
                    <label className="text-xs font-medium text-amber-800 block mb-1">
                      Describe what's in this image:
                    </label>
                    <textarea
                      value={image.text_input || ''}
                      onChange={(e) => {
                        const newImages = images.map(img =>
                          img.s3_key === image.s3_key
                            ? { ...img, text_input: e.target.value }
                            : img
                        );
                        onImagesChange(newImages);
                      }}
                      placeholder="Type the products you see..."
                      rows={2}
                      className="w-full px-3 py-2 border border-amber-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#07E5D1]"
                    />
                    {image.text_input && image.text_input.trim().length > 0 && (
                      <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                        <span className="inline-block w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
                        Saved
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {canAddMore && (
            <button
              type="button"
              onClick={openCamera}
              disabled={disabled}
              className={`w-full py-4 border-2 border-dashed rounded-xl transition-colors
                         flex items-center justify-center gap-2 cursor-pointer
                         ${disabled
                           ? 'opacity-50 cursor-not-allowed border-gray-300 bg-gray-50'
                           : 'border-gray-300 bg-gray-50 hover:bg-gray-100 hover:border-[#07E5D1]'}`}
            >
              <Plus className="w-5 h-5 text-gray-500" />
              <span className="text-gray-600 font-medium">Add Another Photo</span>
            </button>
          )}
        </div>
      )}

      {error && (
        <p className="text-sm text-red-600 text-center">{error}</p>
      )}

      {cameraFallbackReason && (
        // Persistent, unlike `error`: it explains why the button now opens the phone's camera app
        // instead of the in-page one, and that state lasts for the rest of this question.
        <p className="text-xs text-gray-500 text-center">
          {cameraFallbackReason} Your phone's camera app will be used instead.
        </p>
      )}

      {/* Note: Per-image text inputs are now shown directly below each failed image */}

      <p className="text-xs text-gray-500 text-center">
        {images.length} of {maxImages} photos
      </p>

      {images.some(isImageInFlight) && (
        <p className="text-xs text-gray-600 text-center flex items-center justify-center gap-1.5"
           role="status">
          <Loader2 className="w-3 h-3 animate-spin text-[#07E5D1]" />
          Finishing upload — you'll be able to continue in a moment.
        </p>
      )}

      <PhotoValidationModal
        isOpen={validationModal.isOpen}
        error={validationModal.error}
        onRetake={handleRetake}
        onContinueAnyway={handleContinueAnyway}
      />

      {showCamera && (
        <InBrowserCamera
          onCapture={handleCameraCapture}
          onClose={() => setShowCamera(false)}
          onUnavailable={handleCameraUnavailable}
        />
      )}
    </div>
  );
};

export default MobileCameraUpload;
